"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertTriangle,
  Ban,
  CheckCircle,
  CreditCard,
  FileText,
  Mail,
  Shield,
  Users,
} from "lucide-react";
import Link from "next/link";

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 py-12 px-4">
      <div className="container max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-4 py-2 rounded-full mb-4">
            <FileText className="h-5 w-5" />
            <span className="font-semibold">Termos de Serviço</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Agendei - Termos de Serviço
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Última atualização: {new Date().toLocaleDateString("pt-BR")}
          </p>
        </div>

        {/* Introduction */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              <CardTitle>1. Aceitação dos Termos</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Bem-vindo ao <strong>Agendei</strong>! Estes Termos de Serviço
              (&quot;
              <strong>Termos</strong>&quot;) regulam o uso de nossa plataforma
              de agendamento online disponibilizada através do website e
              aplicações relacionadas (coletivamente, a &quot;
              <strong>Plataforma</strong>&quot;).
            </p>
            <p>
              Ao acessar ou usar a Plataforma, você concorda em cumprir e estar
              vinculado a estes Termos. Se você não concordar com qualquer parte
              destes Termos, não deve usar nossa Plataforma.
            </p>
            <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-lg">
              <p className="font-semibold text-amber-900 dark:text-amber-200">
                ⚠️ Importante: Leia atentamente todos os termos antes de
                utilizar nossos serviços.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Definitions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>2. Definições</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-gray-700 dark:text-gray-300">
            <p>Para fins destes Termos:</p>
            <ul className="list-none space-y-2">
              <li>
                <strong>&quot;Plataforma&quot;:</strong> Sistema Agendei e todos
                os seus componentes
              </li>
              <li>
                <strong>
                  &quot;Tenant&quot; ou &quot;Estabelecimento&quot;:
                </strong>{" "}
                Empresa ou profissional que utiliza a Plataforma para gerenciar
                agendamentos
              </li>
              <li>
                <strong>
                  &quot;Usuário Final&quot; ou &quot;Cliente&quot;:
                </strong>{" "}
                Pessoa que agenda serviços através da Plataforma
              </li>
              <li>
                <strong>&quot;Usuário&quot;:</strong> Qualquer pessoa que acessa
                ou utiliza a Plataforma
              </li>
              <li>
                <strong>&quot;Serviços&quot;:</strong> Funcionalidades
                oferecidas pela Plataforma
              </li>
              <li>
                <strong>&quot;Conta&quot;:</strong> Registro de acesso à
                Plataforma
              </li>
              <li>
                <strong>&quot;Assinatura&quot;:</strong> Plano de pagamento
                periódico para uso da Plataforma pelos Tenants
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Services Description */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-5 w-5 text-blue-600" />
              <CardTitle>3. Descrição dos Serviços</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              A Agendei oferece uma plataforma SaaS (Software as a Service) que
              permite:
            </p>

            <div>
              <h4 className="font-semibold mb-2">
                3.1. Para Estabelecimentos (Tenants)
              </h4>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  Criar e gerenciar uma página de agendamento personalizada
                </li>
                <li>Cadastrar e gerenciar serviços oferecidos</li>
                <li>Receber e administrar agendamentos de clientes</li>
                <li>Configurar horários de funcionamento e fechamentos</li>
                <li>Gerenciar base de clientes</li>
                <li>Acompanhar faturamento e relatórios</li>
                <li>Receber e responder avaliações</li>
                <li>Participar do programa de indicações</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">
                3.2. Para Clientes (Usuários Finais)
              </h4>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Agendar serviços online de forma simples</li>
                <li>Visualizar disponibilidade em tempo real</li>
                <li>Receber confirmações e lembretes</li>
                <li>Avaliar serviços prestados</li>
                <li>Gerenciar seus agendamentos</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* User Registration */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>4. Cadastro e Conta de Usuário</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <div>
              <h4 className="font-semibold mb-2">4.1. Requisitos</h4>
              <p>Para usar certos recursos da Plataforma, você deve:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Ter pelo menos 18 anos de idade</li>
                <li>Fornecer informações verdadeiras, precisas e completas</li>
                <li>Manter suas informações atualizadas</li>
                <li>Manter a confidencialidade de sua senha</li>
                <li>
                  Notificar imediatamente sobre uso não autorizado de sua conta
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">
                4.2. Responsabilidade pela Conta
              </h4>
              <p>
                Você é responsável por todas as atividades que ocorrem em sua
                conta. Não compartilhe suas credenciais de acesso com terceiros.
                Não nos responsabilizamos por perdas decorrentes do uso não
                autorizado de sua conta.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">4.3. Encerramento de Conta</h4>
              <p>
                Você pode encerrar sua conta a qualquer momento. Reservamo-nos o
                direito de suspender ou encerrar contas que violem estes Termos,
                sem aviso prévio.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Subscriptions and Payments */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              <CardTitle>5. Assinaturas e Pagamentos (Tenants)</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <div>
              <h4 className="font-semibold mb-2">5.1. Planos de Assinatura</h4>
              <p>
                O acesso aos serviços da Plataforma para Tenants está sujeito ao
                pagamento de uma assinatura mensal. Os valores e condições serão
                informados no momento da contratação.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">5.2. Forma de Pagamento</h4>
              <p>
                Atualmente, os pagamentos são processados manualmente. As
                instruções de pagamento serão fornecidas diretamente pela equipe
                Agendei.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">
                5.3. Renovação e Vencimento
              </h4>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Assinaturas são renovadas mensalmente</li>
                <li>
                  O Tenant é responsável por realizar o pagamento antes da data
                  de vencimento
                </li>
                <li>
                  O não pagamento resultará na suspensão da criação de novos
                  agendamentos
                </li>
                <li>
                  O acesso ao dashboard e visualização de dados permanece
                  disponível durante a suspensão
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">5.4. Política de Reembolso</h4>
              <p>
                Pagamentos realizados não são reembolsáveis, exceto em casos
                específicos determinados exclusivamente pela Agendei ou por
                força de lei.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">
                5.5. Suspensão por Inadimplência
              </h4>
              <div className="bg-red-50 dark:bg-red-950/30 p-4 rounded-lg">
                <p className="text-red-900 dark:text-red-200">
                  Em caso de inadimplência, sua capacidade de criar novos
                  agendamentos será suspensa imediatamente. Para reativar, é
                  necessário regularizar o pagamento em atraso.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Program */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>6. Programa de Indicações</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <div>
              <h4 className="font-semibold mb-2">6.1. Funcionamento</h4>
              <p>
                Tenants podem indicar outros estabelecimentos através de um
                código de indicação único. Quando o estabelecimento indicado
                realizar seu primeiro pagamento de assinatura, o indicador
                receberá uma comissão.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">6.2. Comissões</h4>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>O valor da comissão será definido pela Agendei</li>
                <li>
                  Comissões são creditadas após confirmação do pagamento do
                  indicado
                </li>
                <li>
                  O saque de comissões está sujeito a valores mínimos e
                  procedimentos específicos
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">6.3. Proibições</h4>
              <p>É estritamente proibido:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Usar métodos fraudulentos para gerar indicações</li>
                <li>Criar contas falsas</li>
                <li>Manipular o sistema de indicações</li>
              </ul>
              <p className="mt-2">
                Violações resultarão no cancelamento de comissões e possível
                encerramento da conta.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Reviews and Ratings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>7. Avaliações e Comentários</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <div>
              <h4 className="font-semibold mb-2">7.1. Sistema de Avaliações</h4>
              <p>
                Clientes podem avaliar serviços prestados com notas de 1 a 5
                estrelas e comentários opcionais. Estabelecimentos podem
                responder às avaliações.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">
                7.2. Diretrizes para Avaliações
              </h4>
              <p>Ao postar avaliações, você concorda em:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  Fornecer informações honestas e baseadas em experiências reais
                </li>
                <li>
                  Não publicar conteúdo ofensivo, difamatório ou discriminatório
                </li>
                <li>Não usar linguagem vulgar ou inadequada</li>
                <li>Não incluir informações pessoais de terceiros</li>
                <li>Não violar direitos de propriedade intelectual</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">7.3. Anonimização</h4>
              <p>
                Avaliações públicas exibem o nome do cliente de forma
                parcialmente anonimizada (ex: &quot;João S.&quot;) para proteger
                a privacidade.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">7.4. Moderação</h4>
              <p>
                Reservamo-nos o direito de remover avaliações que violem estas
                diretrizes ou nossa Política de Privacidade, sem aviso prévio.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* User Conduct */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Ban className="h-5 w-5 text-blue-600" />
              <CardTitle>8. Conduta do Usuário</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>Ao usar a Plataforma, você concorda em NÃO:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Violar qualquer lei ou regulamento aplicável</li>
              <li>
                Infringir direitos de propriedade intelectual de terceiros
              </li>
              <li>
                Transmitir conteúdo malicioso, vírus ou códigos prejudiciais
              </li>
              <li>Tentar acessar áreas restritas da Plataforma</li>
              <li>Realizar engenharia reversa ou descompilar o software</li>
              <li>Usar a Plataforma para fins fraudulentos ou ilegais</li>
              <li>Coletar dados de outros usuários sem consentimento</li>
              <li>
                Fazer uso excessivo que prejudique o desempenho da Plataforma
              </li>
              <li>Criar múltiplas contas para abusar de promoções</li>
              <li>Representar-se falsamente como outra pessoa ou entidade</li>
            </ul>
          </CardContent>
        </Card>

        {/* Intellectual Property */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <CardTitle>9. Propriedade Intelectual</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <div>
              <h4 className="font-semibold mb-2">9.1. Direitos da Agendei</h4>
              <p>
                Todo o conteúdo da Plataforma, incluindo mas não limitado a
                texto, gráficos, logos, ícones, imagens, áudio, vídeo, software
                e código, é de propriedade exclusiva da Agendei ou de seus
                licenciadores e está protegido por leis de direitos autorais e
                outras leis de propriedade intelectual.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">9.2. Licença de Uso</h4>
              <p>
                Concedemos a você uma licença limitada, não exclusiva, não
                transferível e revogável para acessar e usar a Plataforma
                estritamente de acordo com estes Termos.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">9.3. Conteúdo do Usuário</h4>
              <p>
                Você mantém todos os direitos sobre o conteúdo que envia à
                Plataforma (logos, banners, descrições). No entanto, ao enviar
                conteúdo, você nos concede uma licença mundial, não exclusiva,
                isenta de royalties para usar, reproduzir, modificar e exibir
                esse conteúdo exclusivamente para fornecer os Serviços.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Liability */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-blue-600" />
              <CardTitle>10. Limitação de Responsabilidade</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <div>
              <h4 className="font-semibold mb-2">
                10.1. Disponibilidade dos Serviços
              </h4>
              <p>
                Embora nos esforcemos para manter a Plataforma disponível 24/7,
                não garantimos que os Serviços estarão ininterruptos, pontuais,
                seguros ou livres de erros. Manutenções e atualizações podem
                causar indisponibilidade temporária.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">
                10.2. &quot;Como Está&quot;
              </h4>
              <p>
                A Plataforma é fornecida &quot;como está&quot; e &quot;conforme
                disponível&quot;, sem garantias de qualquer tipo, expressas ou
                implícitas.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">10.3. Limitação de Danos</h4>
              <p>
                Na máxima extensão permitida por lei, a Agendei não será
                responsável por quaisquer danos indiretos, incidentais,
                especiais, consequenciais ou punitivos, incluindo perda de
                lucros, dados, uso, goodwill ou outras perdas intangíveis.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">
                10.4. Responsabilidade entre Tenants e Clientes
              </h4>
              <p>
                A Agendei é apenas uma plataforma de intermediação. Não somos
                responsáveis por:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Qualidade dos serviços prestados pelos Tenants</li>
                <li>Cancelamentos ou não comparecimentos</li>
                <li>Disputas entre Tenants e Clientes</li>
                <li>Precisão das informações fornecidas pelos Tenants</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">10.5. Backup de Dados</h4>
              <p>
                Embora realizemos backups regulares, recomendamos que você
                mantenha cópias de segurança de seus dados importantes. Não nos
                responsabilizamos por perda de dados.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Indemnification */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>11. Indenização</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Você concorda em indenizar, defender e isentar a Agendei, seus
              diretores, funcionários e agentes de quaisquer reivindicações,
              responsabilidades, danos, perdas e despesas (incluindo honorários
              advocatícios) decorrentes de:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Seu uso da Plataforma</li>
              <li>Violação destes Termos</li>
              <li>Violação de direitos de terceiros</li>
              <li>Conteúdo que você enviar à Plataforma</li>
            </ul>
          </CardContent>
        </Card>

        {/* Termination */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>12. Rescisão</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <div>
              <h4 className="font-semibold mb-2">12.1. Por Você</h4>
              <p>
                Você pode encerrar sua conta a qualquer momento através das
                configurações da conta ou entrando em contato conosco.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">12.2. Por Nós</h4>
              <p>
                Podemos suspender ou encerrar sua conta imediatamente, sem aviso
                prévio, por qualquer motivo, incluindo:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Violação destes Termos</li>
                <li>Inadimplência prolongada</li>
                <li>Atividade fraudulenta ou ilegal</li>
                <li>Solicitação de autoridades legais</li>
                <li>Descontinuação dos Serviços</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">12.3. Efeitos da Rescisão</h4>
              <p>Após a rescisão:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Seu acesso à Plataforma será imediatamente revogado</li>
                <li>
                  Podemos reter dados conforme exigido por lei ou para fins
                  legítimos
                </li>
                <li>Obrigações de pagamento permanecem válidas</li>
                <li>
                  Disposições que por natureza devem sobreviver continuarão em
                  vigor
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Privacy */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>13. Privacidade e Proteção de Dados</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              O uso da Plataforma também é regido por nossa{" "}
              <Link
                href="/privacidade"
                className="text-blue-600 dark:text-blue-400 underline font-semibold"
              >
                Política de Privacidade
              </Link>
              , que descreve como coletamos, usamos e protegemos seus dados
              pessoais em conformidade com a LGPD (Lei Geral de Proteção de
              Dados).
            </p>
            <p>
              Ao usar a Plataforma, você concorda com nossa Política de
              Privacidade.
            </p>
          </CardContent>
        </Card>

        {/* Changes to Terms */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>14. Modificações dos Termos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Reservamo-nos o direito de modificar estes Termos a qualquer
              momento. Quando fizermos alterações significativas, notificaremos
              você através de:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Aviso proeminente na Plataforma</li>
              <li>Email para o endereço cadastrado</li>
            </ul>
            <p className="mt-4">
              Seu uso continuado da Plataforma após as alterações constitui
              aceitação dos novos Termos. Se você não concordar com as
              alterações, deve encerrar sua conta.
            </p>
          </CardContent>
        </Card>

        {/* Governing Law */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>15. Lei Aplicável e Foro</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Estes Termos serão regidos e interpretados de acordo com as leis
              da República Federativa do Brasil, sem considerar conflitos de
              disposições legais.
            </p>
            <p>
              Qualquer disputa relacionada a estes Termos será resolvida
              exclusivamente pelos tribunais da comarca de [CIDADE/ESTADO -
              ATUALIZAR CONFORME LOCALIZAÇÃO DA EMPRESA], ao qual as partes se
              submetem desde já.
            </p>
          </CardContent>
        </Card>

        {/* Miscellaneous */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>16. Disposições Gerais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <div>
              <h4 className="font-semibold mb-2">16.1. Acordo Integral</h4>
              <p>
                Estes Termos, juntamente com a Política de Privacidade,
                constituem o acordo integral entre você e a Agendei relativo ao
                uso da Plataforma.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">16.2. Renúncia</h4>
              <p>
                A falha da Agendei em fazer valer qualquer direito ou disposição
                destes Termos não constituirá renúncia a tal direito ou
                disposição.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">16.3. Divisibilidade</h4>
              <p>
                Se qualquer disposição destes Termos for considerada inválida ou
                inexequível, as demais disposições continuarão em pleno vigor e
                efeito.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">16.4. Cessão</h4>
              <p>
                Você não pode ceder ou transferir estes Termos sem nosso
                consentimento prévio por escrito. Podemos ceder estes Termos a
                qualquer momento sem restrição.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">16.5. Notificações</h4>
              <p>
                Notificações a você podem ser feitas por email ou através de
                avisos na Plataforma. Notificações a nós devem ser enviadas para
                o email de contato fornecido abaixo.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact */}
        <Card className="mb-12">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-5 w-5 text-blue-600" />
              <CardTitle>17. Contato</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              Para questões sobre estes Termos de Serviço, entre em contato:
            </p>
            <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg space-y-2">
              <p>
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:contato@agendei.kayooliveira.com"
                  className="text-blue-600 dark:text-blue-400 underline"
                >
                  contato@agendei.kayooliveira.com
                </a>
              </p>
              <p>
                <strong>Suporte:</strong>{" "}
                <a
                  href="mailto:suporte@agendei.kayooliveira.com"
                  className="text-blue-600 dark:text-blue-400 underline"
                >
                  suporte@agendei.kayooliveira.com
                </a>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-600 dark:text-gray-400 pb-8">
          <p>
            © {new Date().getFullYear()} Agendei. Todos os direitos reservados.
          </p>
          <p className="mt-2">
            Leia também nossa{" "}
            <Link
              href="/privacidade"
              className="text-blue-600 dark:text-blue-400 underline"
            >
              Política de Privacidade
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
